import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { api, ApiError } from '../api/client'
import { Spinner } from '../components/ui'
import { takePendingOAuth } from '../lib/oauth'
import { useAuthStore } from '../stores/auth'

export default function AuthCallbackPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const loginWithIdentityToken = useAuthStore((s) => s.loginWithIdentityToken)
  const [error, setError] = useState<string | null>(null)
  const [providerName, setProviderName] = useState('your identity provider')
  const started = useRef(false)

  useEffect(() => {
    // Guard against StrictMode double-invoke — the auth code is single-use.
    if (started.current) return
    started.current = true

    const pending = takePendingOAuth()
    if (pending) setProviderName(pending.name)

    const code = params.get('code')
    const state = params.get('state')
    const nonce = params.get('nonce')

    if (!pending) {
      setError('No sign-in is in progress. Please start again from the sign-in page.')
      return
    }
    if (!code || !state) {
      setError('The identity provider did not return a complete response.')
      return
    }

    api
      .oauthCallback(pending.id, { code, state, ...(nonce ? { nonce } : {}) })
      .then(async ({ identity_token }) => {
        if (!identity_token) {
          setError('Sign-in failed.')
          return
        }
        await loginWithIdentityToken(identity_token)
        // /login resumes at step 2 (customer scope) when an identity token is present.
        navigate('/login', { replace: true })
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.detail : 'Sign-in failed.')
      })
  }, [params, navigate, loginWithIdentityToken])

  return (
    <div className="flex min-h-screen items-center justify-center bg-ostrel-navy p-6">
      <div className="w-full max-w-md rounded-sm border border-ostrel-border bg-ostrel-surface p-8 text-center">
        {error ? (
          <>
            <h1 className="font-display text-2xl text-ostrel-light">Sign-in failed</h1>
            <p className="mt-4 rounded-sm border border-ostrel-danger/40 bg-ostrel-danger/10 px-3 py-2 text-xs text-ostrel-danger">
              {error}
            </p>
            <Link
              to="/login"
              replace
              className="mt-6 inline-block text-xs text-ostrel-muted transition-colors hover:text-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
            >
              ← Back to sign in
            </Link>
          </>
        ) : (
          <>
            <div className="flex justify-center">
              <Spinner />
            </div>
            <p className="mt-4 text-sm text-ostrel-muted">Signing you in with {providerName}…</p>
          </>
        )}
      </div>
    </div>
  )
}
